import { supabase } from './client';

export const uploadImage = async (file: File, userId: string): Promise<string> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/${Date.now()}.${fileExt}`;
  const filePath = `public/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('note_images')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading image:', uploadError);
    throw uploadError;
  }

  const { data: publicUrlData } = supabase.storage
    .from('note_images')
    .getPublicUrl(filePath);

  return publicUrlData.publicUrl;
};

export const uploadProductImage = async (file: File, userId: string): Promise<string> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/products/${Date.now()}.${fileExt}`;
  const filePath = `public/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('note_images')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading product image:', uploadError);
    throw uploadError;
  }

  const { data: publicUrlData } = supabase.storage
    .from('note_images')
    .getPublicUrl(filePath);

  return publicUrlData.publicUrl;
};

export const uploadPoolProductImage = async (file: File, userId: string): Promise<string> => {
  const fileExt = file.name.split('.').pop() || 'jpg';
  const fileName = `${userId}/${Date.now()}.${fileExt}`;

  const uploadToProductPoolBucket = async () => {
    const { error: uploadError } = await supabase.storage
      .from('product_pool_images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) throw uploadError;

    return supabase.storage.from('product_pool_images').getPublicUrl(fileName).data.publicUrl;
  };

  try {
    return await uploadToProductPoolBucket();
  } catch (primaryError) {
    console.warn('Falling back to note_images bucket for pool upload:', primaryError);
    return uploadProductImage(file, userId);
  }
};

export const uploadStoreAvatar = async (file: File, userId: string): Promise<string> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}/stores/${Date.now()}.${fileExt}`;
  const filePath = `public/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading store avatar:', uploadError);
    throw uploadError;
  }

  const { data: publicUrlData } = supabase.storage
    .from('avatars')
    .getPublicUrl(filePath);

  return publicUrlData.publicUrl;
};

export const deleteImageFromStorage = async (imageUrl: string): Promise<void> => {
  if (!imageUrl) return;

  let bucketName: string | null = null;
  let filePathInBucket: string | null = null;

  if (imageUrl.includes('/note_images/public/')) {
    bucketName = 'note_images';
    filePathInBucket = imageUrl.split('/note_images/')[1];
  } else if (imageUrl.includes('/avatars/')) {
    bucketName = 'avatars';
    filePathInBucket = imageUrl.split('/avatars/')[1];
  } else {
    console.warn('Invalid image URL format for deletion, cannot determine bucket:', imageUrl);
    return;
  }

  if (!bucketName || !filePathInBucket) {
    console.warn('Could not extract bucket or file path for deletion:', imageUrl);
    return;
  }

  const { error } = await supabase.storage
    .from(bucketName)
    .remove([filePathInBucket]);

  if (error) {
    console.error(`Error deleting image from ${bucketName} storage:`, error);
    throw error;
  }
};

/**
 * Constructs the public URL for a file in Supabase Storage
 * This avoids calling getPublicUrl() for every single image which was causing slowdown
 */
const buildPublicStorageUrl = (bucket: string, path: string): string => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/storage/v1/object/public/${bucket}/${path}`;
};

export const getAllUserPhotos = async (userId: string): Promise<string[]> => {
  const photoUrls: Set<string> = new Set();
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // Fetch note images with increased limit (500 instead of 100)
  const { data: noteImagesData, error: noteImagesError } = await supabase.storage
    .from('note_images')
    .list(`public/${userId}`, {
      limit: 500,
      offset: 0,
      sortBy: { column: 'created_at', order: 'desc' },
    });

  if (noteImagesError) {
    console.error('Error listing user note photos:', noteImagesError);
  } else if (noteImagesData && Array.isArray(noteImagesData)) {
    // Process files and folders directly without calling getPublicUrl() for each
    const folders: string[] = [];
    for (const item of noteImagesData) {
      if (!item || item.name === '.emptyFolderPlaceholder') continue;
      
      const isFile = (item as any).type === 'file' || item.name.includes('.');
      if (isFile) {
        // Build URL directly instead of calling getPublicUrl()
        const url = buildPublicStorageUrl('note_images', `public/${userId}/${item.name}`);
        photoUrls.add(url);
      } else {
        // Add folder to process its contents
        folders.push(item.name);
      }
    }

    // Process subfolders efficiently
    if (folders.length > 0) {
      const subfolderPromises = folders.map(folderName =>
        supabase.storage
          .from('note_images')
          .list(`public/${userId}/${folderName}`, { limit: 500, offset: 0 })
      );

      const subfolderResults = await Promise.all(subfolderPromises);
      subfolderResults.forEach((result, idx) => {
        const { data: subFiles } = result;
        if (Array.isArray(subFiles)) {
          subFiles.forEach(f => {
            if (f && f.name !== '.emptyFolderPlaceholder') {
              const url = buildPublicStorageUrl('note_images', `public/${userId}/${folders[idx]}/${f.name}`);
              photoUrls.add(url);
            }
          });
        }
      });
    }
  }

  // Fetch avatar images
  const { data: avatarImagesData, error: avatarImagesError } = await supabase.storage
    .from('avatars')
    .list(`${userId}/`, {
      limit: 500,
      offset: 0,
      sortBy: { column: 'created_at', order: 'desc' },
    });

  if (avatarImagesError) {
    console.error('Error listing user avatar photos:', avatarImagesError);
  } else if (avatarImagesData && Array.isArray(avatarImagesData)) {
    avatarImagesData.forEach(item => {
      if (item && item.name !== '.emptyFolderPlaceholder') {
        const url = buildPublicStorageUrl('avatars', `${userId}/${item.name}`);
        photoUrls.add(url);
      }
    });
  }

  return Array.from(photoUrls).sort((a, b) => b.localeCompare(a));
};